"use client";
import React, { useEffect, useRef } from "react";
import AppImage from "@/components/ui/AppImage";
import { useHomeLanguage } from "./HomeLanguageContext";

export default function PricingSection() {
  const { lang } = useHomeLanguage();
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add("revealed");
        });
      },
      { threshold: 0.08 }
    );
    sectionRef?.current?.querySelectorAll(".animate-on-scroll")?.forEach((el) => observer?.observe(el));
    return () => observer?.disconnect();
  }, []);

  return (
    <section id="pricing" className="py-24 lg:py-28 bg-white relative overflow-hidden" ref={sectionRef}>
      <div className="absolute inset-0 grid-pattern opacity-15 pointer-events-none" />
      {/* Glow orbs */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[700px] h-[400px] bg-gradient-to-b from-primary/5 to-transparent rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 right-0 w-80 h-80 bg-secondary/4 rounded-full blur-3xl pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header */}
        <div className="text-center mb-16 animate-on-scroll">
          <span className="section-label mb-6 inline-flex">
            <span className="w-1.5 h-1.5 rounded-full bg-primary pulse-dot inline-block" />
            {lang === "id" ? "Harga" : "Pricing"}
          </span>
          <h2 className="font-heading text-4xl sm:text-5xl lg:text-[3.25rem] font-bold text-fg leading-[1.1] tracking-[-0.03em] mb-5 mt-5">
            {lang === "id" ? "Hanya " : "Only "}
            <span className="text-[#d70000]">Rp 500rb/{lang === "id" ? "bulan" : "month"}</span>
            {lang === "id" ? " untuk 1 perusahaan" : " for one company"}
          </h2>
          <p className="text-fg-muted text-[17px] max-w-2xl mx-auto leading-relaxed">
            {lang === "id"
              ? "Dapatkan semuanya di Kolabo: HRM, Accounting, CRM, Project Management, dan ratusan modul operasional lain dalam satu platform."
              : "Get everything in Kolabo: HRM, Accounting, CRM, Project Management, and hundreds of operational modules in one platform."}
          </p>
        </div>

        {/* Pricing image */}
        <div className="relative animate-on-scroll scale-in">
          {/* Outer glow */}
          <div className="absolute inset-0 bg-gradient-to-br from-primary/12 to-secondary/8 rounded-2xl blur-2xl scale-105 pointer-events-none" />

          <div className="relative rounded-2xl overflow-hidden pricing-glow border border-primary/15 group">
            <AppImage
              src="/assets/optimized/kolabo-pricing-2026-e3089eb768.webp"
              alt="Tabel harga Kolabo 2026 - paket HRM Accounting CRM lengkap"
              width={1400}
              height={900}
              className="w-full h-auto group-hover:scale-[1.01] transition-transform duration-700"
              sizes="(max-width: 1280px) 100vw, 1280px"
              priority
            />
            <div className="absolute inset-0 pointer-events-none rounded-2xl ring-1 ring-primary/15" />
          </div>
        </div>

        {/* CTA below pricing */}
        <div className="mt-10 animate-on-scroll">
          <div className="relative overflow-hidden rounded-2xl bg-white p-8 lg:p-10 border border-stone-200 shadow-card">
            {/* Animated gradient background */}
            <div className="absolute inset-0 bg-gradient-to-br from-primary/6 via-transparent to-secondary/6 pointer-events-none" />
            <div className="absolute top-0 right-0 w-64 h-64 bg-primary/8 rounded-full blur-3xl pointer-events-none" />
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-secondary/6 rounded-full blur-3xl pointer-events-none" />

            <div className="relative z-10 flex flex-col sm:flex-row items-center justify-between gap-6">
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-cta pulse-dot" />
                  <span className="text-cta text-xs font-bold uppercase tracking-[0.18em]">
                    {lang === "id" ? "Masih Ragu?" : "Still Unsure?"}
                  </span>
                </div>
                <p className="text-fg font-bold text-xl mb-1.5 tracking-tight">{lang === "id" ? "Coba Gratis, Tanpa Ribet!" : "Try Free, No Hassle!"}</p>
                <p className="text-fg-muted text-sm leading-relaxed">
                  {lang === "id"
                    ? "Tanpa kartu kredit, tanpa kewajiban berlangganan, aktif dalam hitungan detik."
                    : "No credit card, no subscription obligation, and active in seconds."}
                </p>
                <div className="grid sm:grid-cols-2 gap-2 mt-4 text-[12px] text-fg-muted">
                  <p className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-primary" />{lang === "id" ? "Akses modul HRM, Accounting, CRM, Project" : "Access HRM, Accounting, CRM, and Project modules"}</p>
                  <p className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-primary" />{lang === "id" ? "Bisa didampingi tim saat onboarding" : "Onboarding can be assisted by our team"}</p>
                  <p className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-primary" />{lang === "id" ? "Data aman, backup harian, dan bisa rollback" : "Secure data, daily backups, and rollback support"}</p>
                  <p className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-primary" />{lang === "id" ? "Menerima custom fitur/modul sesuai kebutuhan" : "Custom feature/module requests are welcome"}</p>
                </div>
              </div>
              <a
                href="#daftar"
                className="btn-gradient text-white font-semibold px-8 py-3.5 rounded-md shadow-brand-lg whitespace-nowrap flex items-center gap-2.5 shrink-0 text-[14px] tracking-wide"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
                </svg>
                {lang === "id" ? "Coba Gratis, Tanpa Ribet!" : "Try Free, No Hassle!"}
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
